import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeAlert, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

export default function BudgetAllocation() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({
    month: currentMonth(),
    year: currentYear(),
    branch_id: 'all',
  });
  const [applyOn, setApplyOn] = useState('monthly_income');
  const [rows, setRows] = useState([]);

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = { month: nextFilters.month || undefined, year: nextFilters.year || undefined };
      if (nextFilters.branch_id && nextFilters.branch_id !== 'all') params.branch_id = nextFilters.branch_id;
      const res = await api.get('/budget/allocations', { params });
      const payload = res.data || {};
      setData(payload);
      setApplyOn(payload.apply_on || 'monthly_income');
      setRows((payload.allocations || []).map((item, index) => ({
        id: item.id || `alloc-${index}`,
        name: item.name || '',
        percentage: item.percentage ?? '',
        is_active: item.is_active !== false,
        sort_order: item.sort_order ?? index + 1,
      })));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load allocations');
      setData(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const singleBranch = activeBranches.length <= 1 || !!data?.single_branch;
  const breakdown = Array.isArray(data?.breakdown) ? data.breakdown : [];
  const summary = data?.summary || {};
  const currentCurrency = user?.default_currency || 'UGX';

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { id: `alloc-${Date.now()}`, name: '', percentage: '', is_active: true, sort_order: prev.length + 1 },
    ]);
  };

  const updateRow = (id, key, value) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const totalPercentage = rows.reduce((sum, row) => sum + Number(row.percentage || 0), 0);
  const isValid = rows.length > 0 && Math.round(totalPercentage * 100) / 100 === 100;

  const save = async () => {
    if (!rows.length) return toast.error('No allocation rules set. Add allocations to begin.');
    if (Math.round(totalPercentage * 100) / 100 !== 100) return toast.error('Total percentage must equal 100%.');
    setSaving(true);
    try {
      const payload = {
        apply_on: applyOn,
        branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || filters.branch_id || 'all') : (filters.branch_id || 'all'),
        allocations: rows.map((row, index) => ({
          id: row.id,
          name: row.name,
          percentage: Number(row.percentage || 0),
          sort_order: index + 1,
          is_active: row.is_active !== false,
        })),
      };
      await api.post('/budget/allocations/bulk-save', payload);
      toast.success('Allocation rules saved');
      await load(filters);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to save allocations');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <BadgeAlert size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Allocation</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={load}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Allocation
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Set percentage allocation rules for savings, rent, marketing, and more.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={() => load(filters)}>Refresh</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={addRow}>
            <Plus size={16} className="mr-1.5" /> Add Allocation
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Allocation Rules</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{summary.allocation_count || rows.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Apply On</p>
            <Select value={applyOn} onValueChange={setApplyOn}>
              <SelectTrigger className="mt-2 bg-white border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="total_income">Total Income</SelectItem>
                <SelectItem value="monthly_income">Monthly Income</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Percentage</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{totalPercentage.toFixed(2)}%</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Calculated Amount</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.total_calculated_amount || 0, currentCurrency)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <Input type="number" min="1" max="12" value={filters.month} onChange={(e) => updateFilter('month', e.target.value)} placeholder="Month" className="bg-white" />
            <Input type="number" min="2020" max="2100" value={filters.year} onChange={(e) => updateFilter('year', e.target.value)} placeholder="Year" className="bg-white" />
            {!singleBranch ? (
              <Select value={filters.branch_id} onValueChange={(value) => updateFilter('branch_id', value)}>
                <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600">
                {activeBranches[0]?.name || 'Main Branch'}
              </div>
            )}
            <div className="flex items-center h-10 px-3 rounded-xl border border-slate-200 bg-slate-50 text-xs text-slate-500">
              {rows.length ? `${rows.length} allocation row(s)` : 'No allocation rules yet'}
            </div>
          </div>
        </CardHeader>
      </Card>

      {!rows.length ? (
        <Card className="border border-dashed border-slate-300 rounded-2xl bg-white">
          <CardContent className="p-8 text-center space-y-4">
            <p className="text-sm text-slate-500">No allocation rules set. Add allocations to begin.</p>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={addRow}>Add Allocation</Button>
          </CardContent>
        </Card>
      ) : null}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Allocation Rules</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{rows.length} row(s)</Badge>
        </CardHeader>
        <CardContent className="space-y-3">
          {rows.map((row, index) => (
            <div key={row.id} className="grid grid-cols-1 gap-3 md:grid-cols-[1.5fr_0.7fr_0.5fr_auto] items-end border border-slate-200 rounded-2xl p-4">
              <div className="grid gap-2">
                <Label>Allocation Name</Label>
                <Input value={row.name} onChange={(e) => updateRow(row.id, 'name', e.target.value)} placeholder="Savings" />
              </div>
              <div className="grid gap-2">
                <Label>Percentage</Label>
                <Input type="number" min="0" max="100" step="0.01" value={row.percentage} onChange={(e) => updateRow(row.id, 'percentage', e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>Active</Label>
                <Select value={row.is_active ? 'true' : 'false'} onValueChange={(value) => updateRow(row.id, 'is_active', value === 'true')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => removeRow(row.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
              <div className="md:col-span-4 flex items-center justify-between text-xs text-slate-500">
                <span>Row {index + 1}</span>
                <span>{Number(row.percentage || 0).toFixed(2)}%</span>
              </div>
            </div>
          ))}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl bg-slate-50 border border-slate-100 p-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Total Percentage: {totalPercentage.toFixed(2)}%</p>
              <p className="text-xs text-slate-500">Save is enabled only when the total equals 100%.</p>
            </div>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={save} disabled={!isValid || saving}>
              {saving ? 'Saving...' : 'Save Allocation'}
            </Button>
          </div>
          {breakdown.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-100">
                    <th className="py-2 pr-4 font-medium">Allocation Name</th>
                    <th className="py-2 pr-4 font-medium">Percentage</th>
                    <th className="py-2 pr-4 font-medium">Calculated Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((item) => (
                    <tr key={item.id || item.name} className="border-b border-slate-50 last:border-0">
                      <td className="py-2 pr-4 text-slate-700">{item.name}</td>
                      <td className="py-2 pr-4 text-slate-700">{Number(item.percentage || 0).toFixed(2)}%</td>
                      <td className="py-2 pr-4 font-medium text-slate-900">{money(item.amount, item.currency || currentCurrency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
